import { Property, Application } from 'springroll';
import { Disposable } from './utils';

// Settings. Not intended to be saved (for now). If saving, consider storing in UserData.
export class Settings
{
    private static _instance: Settings;
    public static get instance() { return this._instance; }

    // ===== Page 1 =====
    /**
     * Settings.TextSize.SMALL
     * Settings.TextSize.MEDIUM
     * Settings.TextSize.LARGE
     */
    public textSize = new Property(Settings.TextSize.MEDIUM);
    /**
     * Settings.TextStyle.WHITE_ON_BLACK
     * Settings.TextStyle.YELLOW_ON_BLACK
     * Settings.TextStyle.BLACK_ON_WHITE
     */
    public textStyle = new Property(Settings.TextStyle.WHITE_ON_BLACK);
    public showBackgroundArt = new Property(true);
    public playAudioDescriptions = new Property(false);
    public settingsOpen = new Property(false);

    // Page 2
    // Sound globally toggled in game's container. These are individual sound-type volumes.
    // Springroll supports these, so if we use the built in Properties instead
    // we can potentially plug into global user settings for free
    /**
     * 0.0->1.0
     */
    // public musicVolume = new Property(0.6);
    /**
     * 0.0->1.0
     */
    // public sfxVolume = new Property(0.6);
    /**
     * 0.0->1.0
     */
    // public voVolume = new Property(0.6);
    /** Captions available in game.app.state. Do we need a toggle within the game? */
    // public captions = new Property(false);
    /**
     * Settings.SfxStyle.SFX_1
     * Settings.SfxStyle.SFX_2
     * Settings.SfxStyle.SFX_3
     */
    public sfxType = new Property(Settings.SfxStyle.SFX_1);

    // Page 3
    public allowCoordinateEntry = new Property(true);
    public useBattery = new Property(true);
    /**
     * Settings.HintFrequency.NONE
     * Settings.HintFrequency.SOME
     * Settings.HintFrequency.EXTRA
     */
    public hintFrequency = new Property(Settings.HintFrequency.SOME);

    public enableDwellClicks = new Property(false);
    public showRestArea = new Property(false);
    public gridGameScene = new Property(false);

    private externalProps: Map<string, Property<any>> = new Map();

    constructor(app:Application)
    {
        Settings._instance = this;

        // grab references to built in Springroll Application props that we want to use
        this.externalProps.set('captionsMuted', app.state.captionsMuted);
        this.externalProps.set('voVolume', app.state.voVolume);
        this.externalProps.set('sfxVolume', app.state.sfxVolume);
        this.externalProps.set('musicVolume', app.state.musicVolume);

        // set up some default values that are undefined if we don't have an external container
        // this code _probably_ will run before a connection is established, in which case these
        // values should get overwritten by user settings
        if (!app.container.connected)
        {
            app.state.voVolume.value = 0.6;
            app.state.sfxVolume.value = 0.6;
            app.state.musicVolume.value = 0.6;
        }

        // read from local storage to load saved settings
        const savedSettings = window.localStorage.getItem('settings');
        const obj = JSON.parse( savedSettings );
        if( obj )
        {
            // Iterate over properties of this class. See if there is a saved value
            // for that property.
            for (let [key, value] of Object.entries(obj))
            {
                if (this.hasOwnProperty(key) && this[key] instanceof Property)
                {
                    this[key].value = value;
                }
                else if (this.externalProps.has(key))
                {
                    this.externalProps.get(key).value = value;
                }
            }
        }

        // set up some variables to immediately overwrite volume settings so that we don't default
        // to 1 when in a container
        const voOverride = app.state.voVolume.value;
        const sfxOverride = app.state.sfxVolume.value;
        const musicOverride = app.state.musicVolume.value;
        // set up listeners to override the first change received
        const voSub = Disposable.fromPropertySubscription(app.state.voVolume, () => {
            app.state.voVolume.value = voOverride;
            voSub.dispose();
        });
        const sfxSub = Disposable.fromPropertySubscription(app.state.sfxVolume, () => {
            app.state.sfxVolume.value = sfxOverride;
            sfxSub.dispose();
        });
        const musicSub = Disposable.fromPropertySubscription(app.state.musicVolume, () => {
            app.state.musicVolume.value = musicOverride;
            musicSub.dispose();
        });
        // allow for 1 second to get conected and receive values, then bail on our listeners
        // (assume we'll never connect)
        setTimeout(() => {
            voSub.dispose();
            sfxSub.dispose();
            musicSub.dispose();
        }, 1000);

        // hook up save listeners, by looping through all the props
        const save = this.save.bind(this);
        for (let [, prop] of Object.entries(this))
        {
            if (prop instanceof Property)
            {
                prop.subscribe(save);
            }
        }
        for (let [, prop] of this.externalProps)
        {
            prop.subscribe(save);
        }
    }

    // Save all settings.
    public save()
    {
        let obj = {};

        // loop through all props on this, find the actual Property instances and record
        // values of those
        for (let [key, prop] of Object.entries(this))
        {
            if (prop instanceof Property)
            {
                obj[key] = prop.value;
            }
        }
        // loop through the external props too
        for (let [key, prop] of this.externalProps)
        {
            obj[key] = prop.value;
        }

        const allSettings = JSON.stringify(obj);
        window.localStorage.setItem( "settings", allSettings );
    }

}

export namespace Settings
{
    // NOTE: constants use these values as keys
    // TextSize and TextStyle must NOT have the same enum values
    export enum TextSize {
        SMALL = 'small',
        MEDIUM = 'medium',
        LARGE = 'large'
    }

    export enum TextStyle {
        WHITE_ON_BLACK = 'wOnB',
        YELLOW_ON_BLACK = 'yOnB',
        BLACK_ON_WHITE = 'bOnW'
    }

    export enum SfxStyle {
        SFX_1 = 'pingSfx1',
        SFX_2 = 'pingSfx2',
        SFX_3 = 'pingSfx3',
    }

    export enum HintFrequency {
        NONE = 'none',
        SOME = 'some',
        EXTRA = 'extra'
    }
}